/**
 * CustomerHealthPanel — Operations Console > Customer Health.
 *
 * Platform-admin only. Shows the health score, status, signals, and
 * recommended next action for every tenant. Sorted highest-risk-first by
 * default. Talks to `/api/customer-health` and `/api/customer-health/*`.
 */
import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import {
  HeartPulse, RefreshCw, Loader2, Search, ChevronRight, X,
  CheckCircle2, AlertTriangle, Activity, Building2,
} from "lucide-react";
import { toast } from "sonner";

export const HEALTH_COLORS = {
  healthy:         "#22c55e",
  active:          "#86efac",
  needs_attention: "#fbbf24",
  stalled:         "#fb923c",
  blocked:         "#fca5a5",
  at_risk:         "#dc2626",
  new:             "#7dd3fc",
  live:            "#22c55e",
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }
  catch { return "—"; }
};

export function HealthChip({ status, score, compact }) {
  const c = HEALTH_COLORS[status] || "#cbd5e1";
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-mono uppercase tracking-wider"
      style={{ background: `${c}1a`, border: `1px solid ${c}66`, color: c }}
      data-testid={`health-chip-${status}`}
    >
      <HeartPulse size={10} />
      {(status || "").replace(/_/g, " ")}
      {!compact && typeof score === "number" && <span className="opacity-75">· {score}</span>}
    </span>
  );
}

export default function CustomerHealthPanel() {
  const [records, setRecords] = useState([]);
  const [counts, setCounts] = useState(null);
  const [statuses, setStatuses] = useState({});
  const [filter, setFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [recomputing, setRecomputing] = useState(false);
  const [active, setActive] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      const { data } = await api.get("/customer-health", { params });
      setRecords(data.records || []);
      setCounts(data.counts || null);
      setStatuses(data.statuses || {});
    } catch (e) {
      const m = e?.response?.data?.detail;
      toast.error(typeof m === "string" ? m : "Failed to load health data");
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const recomputeAll = async () => {
    setRecomputing(true);
    try {
      const { data } = await api.post("/customer-health/recompute", {});
      toast.success(`Recomputed ${data.recomputed} tenants`);
      load();
    } catch (e) {
      const m = e?.response?.data?.detail;
      toast.error(typeof m === "string" ? m : "Recompute failed");
    } finally {
      setRecomputing(false);
    }
  };

  const filtered = records.filter((r) => {
    if (!filter) return true;
    const q = filter.toLowerCase();
    return (
      (r.tenant_display_name || "").toLowerCase().includes(q) ||
      (r.owner_email || "").toLowerCase().includes(q)
    );
  });

  return (
    <div
      className="rounded-xl border bg-[var(--fop-surface)]"
      style={{ borderColor: "var(--fop-border)" }}
      data-testid="customer-health-panel"
    >
      <div className="px-5 py-4 border-b flex items-center justify-between gap-3 flex-wrap"
           style={{ borderColor: "var(--fop-border)" }}>
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-md inline-flex items-center justify-center"
               style={{ background: "rgba(249, 115, 22, 0.10)", border: "1px solid rgba(249, 115, 22, 0.35)" }}>
            <HeartPulse size={16} className="text-[var(--fop-primary)]" />
          </div>
          <div>
            <div className="text-[var(--fop-text)] font-semibold">Customer Health</div>
            <div className="text-xs text-[var(--fop-text-3)] font-mono uppercase tracking-wider">
              Deterministic scoring · platform admin only
            </div>
          </div>
        </div>
        <button
          onClick={recomputeAll}
          disabled={recomputing}
          className="btn btn-ghost"
          style={{ minHeight: "36px", padding: "0 12px" }}
          data-testid="health-recompute-all"
        >
          {recomputing ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
          Recompute all
        </button>
      </div>

      {counts && (
        <div className="px-5 py-3 border-b flex flex-wrap gap-2 text-[11px] font-mono uppercase tracking-wider"
             style={{ borderColor: "var(--fop-border)" }}>
          <Chip label="All" value={counts.total} active={statusFilter === ""} onClick={() => setStatusFilter("")} testid="health-filter-all" />
          {Object.entries(statuses).map(([code, label]) => (
            <Chip
              key={code}
              label={label}
              value={counts[code] || 0}
              active={statusFilter === code}
              onClick={() => setStatusFilter(statusFilter === code ? "" : code)}
              testid={`health-filter-${code}`}
              color={HEALTH_COLORS[code]}
            />
          ))}
        </div>
      )}

      <div className="px-5 py-3 border-b" style={{ borderColor: "var(--fop-border)" }}>
        <div className="relative max-w-md">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--fop-text-3)]" />
          <input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Search company / owner…"
            className="w-full h-10 pl-9 pr-3 rounded-md text-sm"
            data-testid="health-search"
          />
        </div>
      </div>

      {loading ? (
        <div className="p-10 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-[var(--fop-text-3)]" /></div>
      ) : filtered.length === 0 ? (
        <div className="p-12 text-center text-[var(--fop-text-3)] text-sm" data-testid="health-empty">
          No customers match the current filter.
        </div>
      ) : (
        <div className="divide-y" style={{ borderColor: "var(--fop-border)" }}>
          {filtered.map((r) => (
            <Row key={r.tenant_id} record={r} onOpen={() => setActive(r)} />
          ))}
        </div>
      )}

      {active && (
        <HealthDrawer tenantId={active.tenant_id} onClose={() => setActive(null)} onChanged={load} />
      )}
    </div>
  );
}

function Chip({ label, value, active, onClick, testid, color }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className="px-2.5 py-1 rounded transition-colors"
      style={{
        background: active ? "var(--fop-surface-3)" : "var(--fop-surface-2)",
        border: `1px solid ${active ? "var(--fop-primary)" : "var(--fop-border)"}`,
        color: color || "var(--fop-text-2)",
      }}
    >
      {label} · {value}
    </button>
  );
}

function Row({ record, onOpen }) {
  const c = HEALTH_COLORS[record.health_status] || "#cbd5e1";
  return (
    <button
      onClick={onOpen}
      data-testid={`health-row-${record.tenant_id}`}
      className="w-full text-left px-5 py-4 hover:bg-[var(--fop-surface-2)] transition-colors"
    >
      <div className="flex items-center gap-4 flex-wrap">
        <div className="w-14 h-14 rounded-md flex items-center justify-center font-display"
             style={{
               background: `${c}1a`, border: `1px solid ${c}66`, color: c,
               fontSize: "22px",
             }}>
          {record.health_score ?? "—"}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap mb-0.5">
            <span className="font-semibold text-[var(--fop-text)] truncate">{record.tenant_display_name || record.tenant_id}</span>
            <HealthChip status={record.health_status} />
          </div>
          <div className="text-sm text-[var(--fop-text-2)] truncate">
            {record.owner_email || "no owner"} · {record.users_active ?? 0} users · {record.projects_count ?? 0} projects · {record.sheets_count ?? 0} sheets
          </div>
          <div className="text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)] mt-0.5 truncate">
            {record.recommended_action}
          </div>
        </div>
        <div className="text-right text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)]">
          Last activity<br />{fmtDate(record.last_activity_at)}
        </div>
      </div>
    </button>
  );
}

function HealthDrawer({ tenantId, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/customer-health/${tenantId}`);
      setData(data.health);
    } catch (e) {
      const m = e?.response?.data?.detail;
      toast.error(typeof m === "string" ? m : "Failed to load health");
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => { load(); }, [load]);

  const recompute = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/customer-health/${tenantId}/recompute`, {});
      setData(data.health);
      toast.success("Recomputed");
      onChanged?.();
    } catch (e) {
      const m = e?.response?.data?.detail;
      toast.error(typeof m === "string" ? m : "Recompute failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[180] flex items-stretch justify-end"
      style={{ background: "rgba(2, 6, 23, 0.65)", backdropFilter: "blur(6px)" }}
      onClick={onClose}
      data-testid="health-drawer"
    >
      <div
        className="w-full max-w-xl h-full bg-[var(--fop-surface)] border-l border-[var(--fop-border)] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {loading || !data ? (
          <div className="p-12 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-[var(--fop-text-3)]" /></div>
        ) : (
          <>
            <div className="px-6 py-5 border-b flex items-start justify-between gap-4"
                 style={{ borderColor: "var(--fop-border)" }}>
              <div className="min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <Building2 size={14} className="text-[var(--fop-text-3)]" />
                  <div className="font-display text-xl text-[var(--fop-text)] truncate">{data.tenant_display_name || data.tenant_id}</div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <HealthChip status={data.health_status} score={data.health_score} />
                  <span className="text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)]">
                    Updated {fmtDate(data.updated_at)}
                  </span>
                </div>
              </div>
              <button onClick={onClose} className="text-[var(--fop-text-3)] hover:text-[var(--fop-text)]"
                      data-testid="health-drawer-close">
                <X size={20} />
              </button>
            </div>

            <div className="p-6 space-y-6">
              <div className="rounded-lg p-4 flex items-start gap-3"
                   style={{ background: "var(--fop-surface-2)", border: "1px solid var(--fop-border)" }}
                   data-testid="health-recommended">
                <Activity size={16} className="text-[var(--fop-primary)] mt-0.5" />
                <div>
                  <div className="text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)] mb-1">
                    Recommended next action
                  </div>
                  <div className="text-sm text-[var(--fop-text)]">{data.recommended_action}</div>
                </div>
              </div>

              <Stats data={data} />

              <Section title="Positive signals">
                {data.positive_signals?.length ? (
                  data.positive_signals.map((p) => (
                    <Signal key={p.code} icon={CheckCircle2}
                            tone="good"
                            delta={`+${p.delta}`}
                            text={p.text} />
                  ))
                ) : (
                  <div className="text-sm text-[var(--fop-text-3)]">No positive signals yet.</div>
                )}
              </Section>

              <Section title="Risk flags">
                {data.risk_flags?.length ? (
                  data.risk_flags.map((r) => (
                    <Signal key={r.code} icon={AlertTriangle}
                            tone="bad"
                            delta={`${r.delta}`}
                            text={r.text} />
                  ))
                ) : (
                  <div className="text-sm text-[var(--fop-text-3)]">No risk flags.</div>
                )}
              </Section>

              <button onClick={recompute} disabled={busy}
                      className="btn btn-ghost"
                      style={{ minHeight: "40px", padding: "0 14px" }}
                      data-testid="health-drawer-recompute">
                {busy ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                Recompute now
              </button>

              <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-[var(--fop-text-4)] pt-4 border-t"
                   style={{ borderColor: "var(--fop-border)" }}>
                Tenant ID: {data.tenant_id}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Stats({ data }) {
  const stats = [
    { k: "Active users (7d)",   v: data.active_users_7d ?? 0 },
    { k: "Active users (30d)",  v: data.active_users_30d ?? 0 },
    { k: "Projects",            v: data.projects_count ?? 0 },
    { k: "Plan sheets",         v: data.sheets_count ?? 0 },
    { k: "Documents",           v: data.documents_count ?? 0 },
    { k: "Photos",              v: data.photos_count ?? 0 },
    { k: "Open feedback",       v: data.open_feedback_count ?? 0 },
    { k: "Blocked checklist",   v: data.blocked_onboarding_items ?? 0 },
    { k: "Open tasks",          v: data.open_onboarding_tasks ?? 0 },
    { k: "Overdue tasks",       v: data.overdue_onboarding_tasks ?? 0 },
    { k: "Days since activity", v: data.days_since_last_activity ?? "—" },
    { k: "Onboarding status",   v: (data.onboarding_status || "—").replace(/_/g, " ") },
  ];
  return (
    <Section title="Inputs">
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
        {stats.map((s) => (
          <div key={s.k} className="rounded-md p-2.5"
               style={{ background: "var(--fop-surface-2)", border: "1px solid var(--fop-border)" }}>
            <div className="text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)]">{s.k}</div>
            <div className="text-[var(--fop-text)] text-sm mt-0.5 truncate">{s.v}</div>
          </div>
        ))}
      </div>
    </Section>
  );
}

function Signal({ icon: Icon, tone, delta, text }) {
  const tones = {
    good: { color: "var(--fop-success)", bg: "rgba(34,197,94,0.08)",  border: "rgba(34,197,94,0.35)" },
    bad:  { color: "var(--fop-danger)",  bg: "rgba(220,38,38,0.08)",  border: "rgba(220,38,38,0.35)" },
  }[tone] || { color: "var(--fop-text)", bg: "var(--fop-surface-2)", border: "var(--fop-border)" };
  return (
    <div className="rounded-md px-3 py-2 mb-1.5 flex items-center gap-2.5"
         style={{ background: tones.bg, border: `1px solid ${tones.border}` }}>
      <Icon size={14} style={{ color: tones.color }} />
      <div className="flex-1 text-sm text-[var(--fop-text)] min-w-0 truncate">{text}</div>
      <span className="text-[10px] font-mono px-1.5 py-0.5 rounded"
            style={{ background: tones.bg, border: `1px solid ${tones.border}`, color: tones.color }}>
        {delta}
      </span>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-[var(--fop-text-3)] mb-2.5">{title}</div>
      {children}
    </div>
  );
}
